'use client';

import BaseModal from '@c/modals/BaseModal';
import type { ChatMessageDocument } from '@models/chat';
import {
  PaymentRequestDocument,
  PaymentRequestState,
} from '@models/payment-request';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  cancelPaymentRequest,
  createPaymentRequestIntent,
  failedPaymentRequest,
  subscribeToPaymentRequest,
} from '@util/firestore/payment-requests';
import { createCustomer } from '@util/firestore/payments';
import { updateUserByKey } from '@util/firestore/users';
import { STRIPE_ELEMENT_OPTIONS } from '@util/get-stripe';
import { formatCurrency, getHostUrl } from '@util/index';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useStripeContext } from 'context/StripeContext';
import { useToastContext } from 'context/ToastContext';
import { useCustomerSession } from 'features/checkout/hooks/useCustomerSession';
import { CreditCardIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

interface MessagePaymentRequestProps {
  message: ChatMessageDocument;
  isOutbound: boolean;
}

const getBadge = (state: PaymentRequestState) => {
  switch (state) {
    case PaymentRequestState.Paid:
      return { bg: '#1E7A1E1A', fg: '#1E7A1E', text: 'Paid' };
    case PaymentRequestState.Cancelled:
      return { bg: '#F1F1F1', fg: '#444444', text: 'Cancelled' };
    case PaymentRequestState.Failed:
      return { bg: '#F9E8E8', fg: '#C41719', text: 'Payment Failed' };
    default:
      return { bg: '#F1F1F1', fg: '#444444', text: 'Payment Required' };
  }
};

export default function MessagePaymentRequest({
  message,
  isOutbound,
}: MessagePaymentRequestProps) {
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [paymentRequest, setPaymentRequest] =
    useState<PaymentRequestDocument | null>(null);

  const requestId = message.payment_request_id;

  useEffect(() => {
    if (!requestId) return;
    const unsubscribe = subscribeToPaymentRequest(requestId, setPaymentRequest);
    return () => unsubscribe();
  }, [requestId]);

  if (!requestId || !userDoc) return null;
  if (!paymentRequest) {
    return (
      <div
        className={`mb-8 flex w-fit flex-col ${
          isOutbound ? 'ml-auto pr-4' : 'mr-auto'
        }`}
      >
        <div className="h-[16rem] w-[38rem] animate-pulse rounded-[1.5rem] bg-zinc-100" />
      </div>
    );
  }

  const isPayer = paymentRequest.payer_uid === userDoc.uid;
  const isRequester = paymentRequest.requester_uid === userDoc.uid;
  const canCancel =
    isRequester && paymentRequest.state === PaymentRequestState.Pending;
  const badge = getBadge(paymentRequest.state);
  const isPending = paymentRequest.state === PaymentRequestState.Pending;
  const isFailed = paymentRequest.state === PaymentRequestState.Failed;
  const showPay = isPayer && (isPending || isFailed);

  const handleCancel = async () => {
    if (!canCancel) return;
    try {
      await cancelPaymentRequest(paymentRequest.id);
      showSuccessToast('Payment request cancelled');
    } catch (e) {
      logError(e);
      showErrorToast('Could not cancel payment request');
    }
  };

  return (
    <div
      className={`mb-8 flex w-fit flex-col ${
        isOutbound ? 'ml-auto pr-4' : 'mr-auto'
      }`}
    >
      <div className="w-[38rem] space-y-6 rounded-[1.5rem] border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div
            className="rounded-full px-4 py-1 text-[1.4rem] font-semibold"
            style={{ backgroundColor: badge.bg, color: badge.fg }}
          >
            {badge.text}
          </div>

          {canCancel && (
            <button
              type="button"
              className="border-none bg-inherit text-[1.4rem] font-semibold text-brand-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
        </div>

        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-red-50">
            <CreditCardIcon className="h-9 w-9 text-brand-secondary" />
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="text-[2rem] font-bold text-black">
              {formatCurrency(paymentRequest.amount)}
            </h2>
            <p className="text-[1.5rem] text-zinc-600">
              {paymentRequest.description || 'Tap to pay securely with Stripe'}
            </p>
          </div>
        </div>

        {isFailed && paymentRequest.failure_reason && (
          <div className="rounded-[1rem] bg-red-50 px-4 py-3 text-[1.4rem] text-red-700">
            {paymentRequest.failure_reason}
          </div>
        )}

        {showPay && (
          <PayWithStripe
            request={paymentRequest}
            ctaLabel={isFailed ? 'Try Again' : 'Pay Now'}
            onPaid={() => {
              setPaymentRequest((prev) =>
                prev
                  ? {
                      ...prev,
                      state: PaymentRequestState.Paid,
                      paid_at: Date.now(),
                      failure_reason: null,
                    }
                  : prev
              );
              showSuccessToast(`Paid ${formatCurrency(paymentRequest.amount)}`);
            }}
          />
        )}
      </div>

      {Boolean(message.created_at) && (
        <p
          className={`mt-4 text-[1.5rem] text-brand-gray ${
            isOutbound ? 'self-end' : 'self-start'
          }`}
        >
          {new Date(message.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      )}
    </div>
  );
}

type StripeIntent = {
  paymentIntentClientSecret: string;
  customerId: string;
};

function PayWithStripe({
  request,
  ctaLabel,
  onPaid,
}: {
  request: PaymentRequestDocument;
  ctaLabel: string;
  onPaid: () => void;
}) {
  const { userDoc } = useAuth();
  const { stripePromise } = useStripeContext();
  const { showErrorToast } = useToastContext();
  const { data: customerSession } = useCustomerSession();
  const [isOpen, setIsOpen] = useState(false);
  const [intent, setIntent] = useState<StripeIntent | null>(null);
  const [preparing, setPreparing] = useState(false);

  const open = async () => {
    if (!userDoc) return;
    setPreparing(true);
    try {
      if (!userDoc.customer_id) {
        const newCustomerId = await createCustomer();
        await updateUserByKey(userDoc.uid, 'customer_id', newCustomerId);
      }
      const i = await createPaymentRequestIntent(request.id);
      setIntent({
        paymentIntentClientSecret: i.paymentIntentClientSecret,
        customerId: i.customerId,
      });
      setIsOpen(true);
    } catch (e) {
      logError(e);
      showErrorToast(
        (e as Error)?.message ?? 'Could not start payment. Try again.'
      );
    } finally {
      setPreparing(false);
    }
  };

  const close = () => {
    setIsOpen(false);
    setIntent(null);
  };

  return (
    <>
      <button
        type="button"
        disabled={preparing}
        onClick={open}
        className="flex w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary p-4 text-[1.8rem] font-semibold text-white disabled:opacity-60"
      >
        {preparing ? 'Loading…' : ctaLabel}
      </button>

      <BaseModal isOpen={isOpen} dismiss={close} title="Complete payment">
        <div className="flex w-[44rem] max-w-full flex-col gap-[2rem] p-8">
          <div className="flex items-center justify-between text-[1.6rem]">
            <span className="text-zinc-600">Amount due</span>
            <span className="font-bold text-black">
              {formatCurrency(request.amount)}
            </span>
          </div>

          {intent && (
            <Elements
              stripe={stripePromise}
              options={{
                ...STRIPE_ELEMENT_OPTIONS,
                clientSecret: intent.paymentIntentClientSecret,
                ...(customerSession?.data && {
                  customerSessionClientSecret: customerSession.data,
                }),
              }}
            >
              <PaymentForm
                requestId={request.id}
                onSuccess={() => {
                  onPaid();
                  close();
                }}
                onFailure={(reason) => {
                  failedPaymentRequest(request.id, {
                    state: PaymentRequestState.Failed,
                    failure_reason: reason,
                  }).catch(logError);
                }}
              />
            </Elements>
          )}
        </div>
      </BaseModal>
    </>
  );
}

function PaymentForm({
  requestId,
  onSuccess,
  onFailure,
}: {
  requestId: string;
  onSuccess: () => void;
  onFailure: (reason: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { showErrorToast } = useToastContext();
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showErrorToast(submitError.message ?? 'Form invalid');
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${getHostUrl()}/dashboard/messages?payment_request=${requestId}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        const reason = error.message ?? 'Payment failed';
        showErrorToast(reason);
        onFailure(reason);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess();
      } else if (paymentIntent?.status === 'processing') {
        // Backend webhook will mark as Paid; treat as success in UI.
        onSuccess();
      } else {
        const reason = `Unexpected payment status: ${
          paymentIntent?.status ?? 'unknown'
        }`;
        showErrorToast(reason);
        onFailure(reason);
      }
    } catch (e) {
      logError(e);
      const reason = (e as Error)?.message ?? 'Payment failed';
      showErrorToast(reason);
      onFailure(reason);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <PaymentElement />
      <button
        type="button"
        disabled={!stripe || submitting}
        onClick={submit}
        className="flex w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary p-4 text-[1.8rem] font-semibold text-white disabled:opacity-60"
      >
        {submitting ? 'Processing…' : 'Pay'}
      </button>
    </div>
  );
}
